import React, { useState, useEffect } from 'react';
import { Modal, Tabs, Button, Input, Tag } from 'antd';
import { useI18n } from '../../i18n/useI18n';
import type { ProgramStatusData, ProgramStatusMessage, ProgramStatusResponseMessage, HamlibControlRequestMessage, HamlibControlResponseMessage } from './amplifier';
import ProtocolModeModal from './ProtocolModeModal';

interface SystemSyncModalProps {
    isOpen: boolean;
    onClose: () => void;
    /** MQTT 下发的 update_program_status 消息，用于展示 data 并组包 response */
    programStatusMessage?: ProgramStatusMessage | null;
    /** 失焦时将修改后的 data 封装为 ProgramStatusResponseMessage 通过此回调发送到 MQTT */
    onProgramStatusSave?: (message: ProgramStatusResponseMessage) => void;
    /** Hamlib set_hamlib 请求消息，用于组包 response 发送 command=0 等 */
    hamlibControlRequestMessage?: HamlibControlRequestMessage | null;
    /** 发送 Hamlib 响应到 MQTT（如 重新连接 command=0） */
    onHamlibSave?: (message: HamlibControlResponseMessage) => void;
}

export const SystemSyncModal: React.FC<SystemSyncModalProps> = ({
    isOpen,
    onClose,
    programStatusMessage,
    onProgramStatusSave,
    hamlibControlRequestMessage,
    onHamlibSave
}) => {
    const { t } = useI18n();
    const programStatusData = programStatusMessage?.data;

    const [activeTab, setActiveTab] = useState('network');
    const [ampActiveTab, setAmpActiveTab] = useState('client');
    const [isProtocolModeModalOpen, setIsProtocolModeModalOpen] = useState(false);
    /** MQTT 收到的 set_hamlib 消息的 data；若后端把字段放在消息根上则兼容取根对象 */
    const hamlibData: { server_ip?: string; server_port?: number; [k: string]: any } | undefined =
        hamlibControlRequestMessage?.data ?? (hamlibControlRequestMessage && 'server_ip' in hamlibControlRequestMessage ? (hamlibControlRequestMessage as unknown as { server_ip?: string; server_port?: number }) : undefined);
    const [serialPortLocal, setSerialPortLocal] = useState<string>(programStatusData?.serial_port ?? '');
    useEffect(() => {
        setSerialPortLocal(programStatusData?.serial_port ?? '');
    }, [programStatusData?.serial_port]);

    const [mqttIpLocal, setMqttIpLocal] = useState<string>(programStatusData?.mqtt_ip ?? '');
    const [mqttPortLocal, setMqttPortLocal] = useState<string>(programStatusData?.mqtt_port != null ? String(programStatusData.mqtt_port) : '');
    const [mqttClientIdLocal, setMqttClientIdLocal] = useState<string>(programStatusData?.mqtt_clientid ?? '');
    const [mqttUsernameLocal, setMqttUsernameLocal] = useState<string>(programStatusData?.mqtt_username ?? '');
    const [mqttPasswordLocal, setMqttPasswordLocal] = useState<string>(programStatusData?.mqtt_password ?? '');
    useEffect(() => {
        setMqttIpLocal(programStatusData?.mqtt_ip ?? '');
        setMqttPortLocal(programStatusData?.mqtt_port != null ? String(programStatusData.mqtt_port) : '');
        setMqttClientIdLocal(programStatusData?.mqtt_clientid ?? '');
        setMqttUsernameLocal(programStatusData?.mqtt_username ?? '');
        setMqttPasswordLocal(programStatusData?.mqtt_password ?? '');
    }, [programStatusData?.mqtt_ip, programStatusData?.mqtt_port, programStatusData?.mqtt_clientid, programStatusData?.mqtt_username, programStatusData?.mqtt_password]);

    const [isMqttConfigModalOpen, setIsMqttConfigModalOpen] = useState(false);
    const [mqttFormIp, setMqttFormIp] = useState('');
    const [mqttFormPort, setMqttFormPort] = useState('');
    const [mqttFormClientId, setMqttFormClientId] = useState('');
    const [mqttFormUsername, setMqttFormUsername] = useState('');
    const [mqttFormPassword, setMqttFormPassword] = useState('');
    const openMqttConfigModal = () => {
        setMqttFormIp(mqttIpLocal);
        setMqttFormPort(mqttPortLocal);
        setMqttFormClientId(mqttClientIdLocal);
        setMqttFormUsername(mqttUsernameLocal);
        setMqttFormPassword(mqttPasswordLocal);
        setIsMqttConfigModalOpen(true);
    };
    const submitMqttConfig = () => {
        const portNum = mqttFormPort.trim() === '' ? programStatusData?.mqtt_port : Number(mqttFormPort);
        const mqttPort = (typeof portNum === 'number' && !Number.isNaN(portNum)) ? portNum : (programStatusData?.mqtt_port ?? 0);
        buildProgramStatusResponse({
            mqtt_ip: mqttFormIp,
            mqtt_port: mqttPort,
            mqtt_clientid: mqttFormClientId,
            mqtt_username: mqttFormUsername,
            mqtt_password: mqttFormPassword,
        });
        setIsMqttConfigModalOpen(false);
    };

    const buildProgramStatusResponse = (overrides: {
        serial_port?: string;
        command?: number;
        mqtt_ip?: string;
        mqtt_port?: number;
        mqtt_clientid?: string;
        mqtt_username?: string;
        mqtt_password?: string;
    }) => {
        if (!programStatusMessage?.data || !onProgramStatusSave) return;
        const responseMessage: ProgramStatusResponseMessage = {
            ...programStatusMessage,
            type: 'response',
            cmd: 'update_program_status',
            timestamp: Date.now(),
            data: {
                ...programStatusMessage.data,
                ...overrides,
            },
        };
        onProgramStatusSave(responseMessage);
    };

    const renderConnectionStatus = (connected: boolean | undefined, labelConnected: string, labelDisconnected: string) => (
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px' }}>
            <span style={{ color: 'white' }}>连接:</span>
            <Tag color={connected ? 'green' : 'red'}>{connected ? labelConnected : labelDisconnected}</Tag>
        </div>
    );

    return (
        <Modal
            title="系统同步"
            open={isOpen}
            onCancel={onClose}
            footer={[
                <Button key="cancel" onClick={onClose}>
                    取消
                </Button>,
                <Button key="confirm" type="primary" onClick={onClose}>
                    确认
                </Button>
            ]}
            width={800}
        >
            <div style={{ display: 'flex', gap: '20px' }}>
                {/* Hamlib连接部分 */}
                <div style={{ flex: 1, backgroundColor: '#1a1a1a', padding: '20px', borderRadius: '8px' }}>
                    <h3 style={{ color: 'white', marginBottom: '20px' }}>Hamlib连接</h3>
                    <Tabs
                        activeKey={activeTab}
                        onChange={setActiveTab}
                        style={{ marginBottom: '20px' }}
                        tabBarStyle={{
                            backgroundColor: '#2a2a2a',
                            borderBottom: '1px solid #444'
                        }}
                        items={[
                            {
                                key: 'network',
                                label: <span style={{ color: activeTab === 'network' ? '#fff' : '#ccc' }}>网络</span>,
                                children: (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <span style={{ color: 'white' }}>IP:</span>
                                            <span style={{ color: 'white', minWidth: 200 }}>{hamlibData?.server_ip ?? '—'}</span>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <span style={{ color: 'white' }}>PORT:</span>
                                            <span style={{ color: 'white', minWidth: 200 }}>{hamlibData?.server_port != null ? String(hamlibData.server_port) : '—'}</span>
                                        </div>
                                    </div>
                                )
                            },
                            {
                                key: 'serial',
                                label: <span style={{ color: activeTab === 'serial' ? '#fff' : '#ccc' }}>串口</span>,
                                children: (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <span style={{ color: 'white' }}>PORT:</span>
                                            <span style={{ color: 'white', minWidth: 200 }}>{serialPortLocal || '—'}</span>
                                        </div>
                                    </div>
                                )
                            }
                        ]}
                    />
                    {renderConnectionStatus(programStatusData?.hamlib_connection, '已连接', '未连接')}
                    <Button
                        style={{ width: '100%', marginTop: '20px', backgroundColor: '#333', borderColor: '#555', color: 'white' }}
                        onClick={() => setIsProtocolModeModalOpen(true)}
                    >
                        重新连接
                    </Button>
                </div>

                <ProtocolModeModal
                    isOpen={isProtocolModeModalOpen}
                    onClose={() => setIsProtocolModeModalOpen(false)}
                    hamlibControlRequestMessage={hamlibControlRequestMessage}
                    onHamlibSave={onHamlibSave}
                    onCommandClick={(cmd) => {
                        if (!hamlibControlRequestMessage?.data || !onHamlibSave) return;
                        onHamlibSave({
                            ...hamlibControlRequestMessage,
                            type: 'response',
                            cmd: 'set_hamlib',
                            timestamp: Date.now(),
                            data: { ...hamlibControlRequestMessage.data, command: cmd },
                        });
                    }}
                />
                {/* 功放部分 */}
                <div style={{ flex: 1, backgroundColor: '#1a1a1a', padding: '20px', borderRadius: '8px' }}>
                    <h3 style={{ color: 'white', marginBottom: '20px' }}>功放</h3>
                    <Tabs
                        activeKey={ampActiveTab}
                        onChange={setAmpActiveTab}
                        style={{ marginBottom: '20px' }}
                        tabBarStyle={{
                            backgroundColor: '#2a2a2a',
                            borderBottom: '1px solid #444'
                        }}
                        items={[
                            {
                                key: 'client',
                                label: <span style={{ color: ampActiveTab === 'client' ? '#fff' : '#ccc' }}>客户端</span>,
                                children: (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <span style={{ color: 'white' }}>硬件版本:</span>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                <span style={{ color: 'white' }}>{programStatusData?.firmware_version ?? '—'}{(programStatusData?.firmware_updateable) ? '(Updateable)' : ''}</span>
                                                <Button style={{ backgroundColor: '#333', borderColor: '#555', color: 'white' }} onClick={() => buildProgramStatusResponse({ command: 5 })}>更新</Button>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <span style={{ color: 'white' }}>软件版本:</span>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                <span style={{ color: 'white' }}>{programStatusData?.software_version ?? '—'}{(programStatusData?.software_updateable) ? '(Updateable)' : ''}</span>
                                                <Button style={{ backgroundColor: '#333', borderColor: '#555', color: 'white' }} onClick={() => buildProgramStatusResponse({ command: 6 })}>更新</Button>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <span style={{ color: 'white' }}>串口:</span>
                                            <span style={{ color: 'white' }}>{programStatusData?.serial_port ?? '—'}</span>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <span style={{ color: 'white' }}>连接:</span>
                                            <Tag color={programStatusData?.serial_connection ? 'green' : 'red'}>{programStatusData?.serial_connection ? '已连接' : '未连接'}</Tag>
                                        </div>
                                        <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                                            <Button style={{ flex: 1, backgroundColor: '#333', borderColor: '#555', color: 'white' }} onClick={() => buildProgramStatusResponse({ command: 4 })}>检查版本</Button>
                                            <Button style={{ flex: 1, backgroundColor: '#333', borderColor: '#555', color: 'white' }} onClick={() => buildProgramStatusResponse({ command: 1 })}>重新连接</Button>
                                        </div>
                                    </div>
                                )
                            },
                            {
                                key: 'server',
                                label: <span style={{ color: ampActiveTab === 'server' ? '#fff' : '#ccc' }}>服务器</span>,
                                children: (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <span style={{ color: 'white' }}>IP:</span>
                                            <span style={{ color: 'white', minWidth: 200 }}>{mqttIpLocal || '—'}</span>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <span style={{ color: 'white' }}>PORT:</span>
                                            <span style={{ color: 'white', minWidth: 200 }}>{mqttPortLocal || '—'}</span>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <span style={{ color: 'white' }}>ClientID:</span>
                                            <span style={{ color: 'white', minWidth: 200 }}>{mqttClientIdLocal || '—'}</span>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <span style={{ color: 'white' }}>UserName:</span>
                                            <span style={{ color: 'white', minWidth: 200 }}>{mqttUsernameLocal || '—'}</span>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <span style={{ color: 'white' }}>PassWd:</span>
                                            <span style={{ color: 'white', minWidth: 200 }}>{mqttPasswordLocal ? '••••••' : '—'}</span>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <span style={{ color: 'white' }}>连接:</span>
                                            <Tag color={programStatusData?.mqtt_connection ? 'green' : 'red'}>{programStatusData?.mqtt_connection ? '已连接' : '未连接'}</Tag>
                                        </div>
                                        <Button
                                            style={{ width: '100%', marginTop: '10px', backgroundColor: '#333', borderColor: '#555', color: 'white' }}
                                            onClick={openMqttConfigModal}
                                        >
                                            配置
                                        </Button>
                                    </div>
                                )
                            }
                        ]}
                    />
                </div>
            </div>

            <Modal
                title="MQTT 配置"
                open={isMqttConfigModalOpen}
                onCancel={() => setIsMqttConfigModalOpen(false)}
                footer={[
                    <Button key="cancel" onClick={() => setIsMqttConfigModalOpen(false)}>取消</Button>,
                    <Button key="confirm" type="primary" onClick={submitMqttConfig}>确认</Button>,
                ]}
                width={480}
                destroyOnClose
                styles={{ body: { backgroundColor: '#1a1a1a' } }}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: 'white' }}>Host / IP :</span>
                        <Input
                            value={mqttFormIp}
                            onChange={(e) => setMqttFormIp(e.target.value)}
                            style={{ width: 260, backgroundColor: '#333', borderColor: '#555', color: 'white' }}
                        />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: 'white' }}>Port :</span>
                        <Input
                            value={mqttFormPort}
                            onChange={(e) => setMqttFormPort(e.target.value)}
                            style={{ width: 260, backgroundColor: '#333', borderColor: '#555', color: 'white' }}
                        />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: 'white' }}>ClientId :</span>
                        <Input
                            value={mqttFormClientId}
                            onChange={(e) => setMqttFormClientId(e.target.value)}
                            style={{ width: 260, backgroundColor: '#333', borderColor: '#555', color: 'white' }}
                        />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: 'white' }}>Username :</span>
                        <Input
                            value={mqttFormUsername}
                            onChange={(e) => setMqttFormUsername(e.target.value)}
                            style={{ width: 260, backgroundColor: '#333', borderColor: '#555', color: 'white' }}
                        />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: 'white' }}>Password :</span>
                        <Input
                            value={mqttFormPassword}
                            onChange={(e) => setMqttFormPassword(e.target.value)}
                            style={{ width: 260, backgroundColor: '#333', borderColor: '#555', color: 'white' }}
                        />
                    </div>
                </div>
            </Modal>
        </Modal>
    );
};

export default SystemSyncModal;